/**
 * Drag and drop in the rundown tree, decided purely: where a dragged row (or
 * block of rows) lands, whether that is allowed, and the `ord` keys it gets.
 *
 * **The parent rules are the domain's** (`ALLOWED_PARENT_KINDS`, PLAN §5.1:
 * a part hangs under a segment, a piece under a part, a segment under the
 * rundown), so the editor never offers a drop the server would nack.
 *
 * **Where a drop lands**, given the row under the pointer and whether the
 * pointer is in its top part (`before`), bottom part (`after`) or middle
 * (`inside`):
 *   - over a row of the dragged kind → beside it, under the same parent;
 *   - over a row of an allowed parent kind → into it: at the end for
 *     `inside`, first for `before`/`after` (a segment header's "after" is its
 *     first slot, where the eye expects it);
 *   - over a leaf row of another kind (a piece or an adlib) that hangs under
 *     a legal parent of the dragged kind (a piece over an adlib of the same
 *     part, a segment over a global adlib) → last in that parent — never
 *     over a container (an adlib dropped on a segment header does not turn
 *     global);
 *   - over a descendant of a row of the dragged kind (a piece while dragging
 *     parts) → after that ancestor;
 *   - anything else, or a drop onto the dragged rows themselves → no drop.
 *
 * **Siblings are counted per kind.** A part holds pieces and adlibs, a
 * rundown segments and global adlibs; the tree lists each kind as its own
 * group (`viewModel.ts`), so a slot index and the `ord` neighbours are taken
 * among the children of the dragged kind only — an adlib whose `ord` sits
 * between two pieces does not split them.
 *
 * **A block** (multi-select): all dragged rows must be one kind; they land
 * adjacent, in their current display order, with `ordsBetween` keys strictly
 * between the landing neighbours (PLAN §6.2 "the rows end up adjacent in the
 * same relative order"). The neighbours are computed without the dragged
 * rows, so moving a block down past itself works.
 */
import { ALLOWED_PARENT_KINDS, type RowKind } from '@acp/domain/fields.js';
import { ordsBetween } from '@acp/domain/ordering.js';
import type { Row } from '@acp/domain/row.js';
import { childrenOf, type RowTree } from '@acp/domain/tree.js';
import type { Move } from './patches.js';

export type DropPosition = 'before' | 'after' | 'inside';

export type DropTarget = { readonly overId: string; readonly position: DropPosition };

/** Where the block goes: a parent and an index among the parent's children that are not being dragged. */
export type DropSlot = { readonly parentId: string; readonly index: number };

/** The row under the pointer's vertical position: top quarter before, bottom quarter after, middle inside. */
export function positionFromPointer(offsetY: number, height: number, canNest: boolean): DropPosition {
  if (!canNest) return offsetY < height / 2 ? 'before' : 'after';
  if (offsetY < height / 4) return 'before';
  if (offsetY > (height * 3) / 4) return 'after';
  return 'inside';
}

function kindOf(tree: RowTree, id: string): RowKind | null {
  return tree.rows.get(id)?.kind ?? null;
}

/** The ids of the rows being dragged, in display order, if they are one kind and all exist; else null. */
export function orderedBlock(tree: RowTree, displayOrder: readonly string[], dragIds: readonly string[]): { readonly kind: RowKind; readonly ids: readonly string[] } | null {
  const set = new Set(dragIds);
  const ids = displayOrder.filter((id) => set.has(id));
  if (ids.length === 0 || ids.length !== set.size) return null;
  const kinds = new Set(ids.map((id) => kindOf(tree, id)));
  const [kind] = kinds;
  if (kinds.size !== 1 || kind === undefined || kind === null) return null;
  return { kind, ids };
}

/** The ancestor chain of `id` (itself first). */
function ancestry(tree: RowTree, id: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let current: string | null = id;
  while (current !== null && !seen.has(current)) {
    seen.add(current);
    out.push(current);
    current = tree.rows.get(current)?.parentId ?? null;
  }
  return out;
}

/** Kinds that hold no rows: dropping onto one means "beside it", never "into it". */
const LEAF_KINDS: readonly RowKind[] = ['piece', 'adlib'];

/** The children of `parentId` of one kind, in `ord` order. */
export function childrenOfKind(tree: RowTree, parentId: string, kind: RowKind): readonly Row[] {
  return childrenOf(tree, parentId).filter((r) => r.kind === kind);
}

function siblingsWithout(tree: RowTree, parentId: string, kind: RowKind, dragged: ReadonlySet<string>): readonly string[] {
  return childrenOfKind(tree, parentId, kind)
    .map((r) => r.rowId)
    .filter((id) => !dragged.has(id));
}

function besideSlot(tree: RowTree, anchorId: string, after: boolean, dragged: ReadonlySet<string>): DropSlot | null {
  const anchor = tree.rows.get(anchorId);
  const parentId = anchor?.parentId ?? null;
  if (anchor === undefined || parentId === null) return null;
  const siblings = siblingsWithout(tree, parentId, anchor.kind, dragged);
  const index = siblings.indexOf(anchorId);
  if (index < 0) return null;
  return { parentId, index: after ? index + 1 : index };
}

/** Rule table of the module docstring. Null: not a legal drop. */
export function dropSlot(tree: RowTree, kind: RowKind, dragIds: readonly string[], target: DropTarget): DropSlot | null {
  const dragged = new Set(dragIds);
  const chain = ancestry(tree, target.overId);
  if (chain.some((id) => dragged.has(id))) return null;
  const overKind = kindOf(tree, target.overId);
  if (overKind === null) return null;
  if (overKind === kind) return besideSlot(tree, target.overId, target.position !== 'before', dragged);
  if (ALLOWED_PARENT_KINDS[kind].includes(overKind)) {
    const siblings = siblingsWithout(tree, target.overId, kind, dragged);
    return { parentId: target.overId, index: target.position === 'inside' ? siblings.length : 0 };
  }
  const overParent = tree.rows.get(target.overId)?.parentId ?? null;
  const overParentKind = overParent === null ? null : kindOf(tree, overParent);
  if (LEAF_KINDS.includes(overKind) && overParent !== null && overParentKind !== null && ALLOWED_PARENT_KINDS[kind].includes(overParentKind)) {
    return { parentId: overParent, index: siblingsWithout(tree, overParent, kind, dragged).length };
  }
  const ancestor = chain.find((id) => kindOf(tree, id) === kind);
  return ancestor === undefined ? null : besideSlot(tree, ancestor, true, dragged);
}

/**
 * The moves for dropping `dragIds` at `target`: each row's new parent and
 * `ord`, in display order. Null when the drop is illegal, or changes nothing
 * (the block is already exactly there).
 */
export function planDrop(tree: RowTree, displayOrder: readonly string[], dragIds: readonly string[], target: DropTarget, writerTag: string): readonly Move[] | null {
  const block = orderedBlock(tree, displayOrder, dragIds);
  if (block === null) return null;
  const slot = dropSlot(tree, block.kind, block.ids, target);
  if (slot === null) return null;
  const dragged = new Set(block.ids);
  const siblings = childrenOfKind(tree, slot.parentId, block.kind).filter((r) => !dragged.has(r.rowId));
  const before = slot.index > 0 ? (siblings[slot.index - 1] ?? null) : null;
  const after = siblings[slot.index] ?? null;
  if (isAlreadyThere(tree, slot.parentId, block.kind, block.ids, before?.rowId ?? null, after?.rowId ?? null)) return null;
  const ords = ordsBetween(before?.ord ?? null, after?.ord ?? null, block.ids.length, writerTag);
  return block.ids.map((rowId, i) => ({ rowId, parentId: slot.parentId, ord: ords[i] ?? '' }));
}

/** Whether the block already sits contiguously, in order, between `beforeId` and `afterId` under `parentId`. */
function isAlreadyThere(tree: RowTree, parentId: string, kind: RowKind, ids: readonly string[], beforeId: string | null, afterId: string | null): boolean {
  const children = childrenOfKind(tree, parentId, kind).map((r) => r.rowId);
  const start = children.indexOf(ids[0] ?? '');
  if (start < 0) return false;
  for (let i = 0; i < ids.length; i += 1) if (children[start + i] !== ids[i]) return false;
  return (children[start - 1] ?? null) === beforeId && (children[start + ids.length] ?? null) === afterId;
}
